import getConfig from 'next/config';
import Bugsnag from '@bugsnag/js';
import { buildApiUrl } from '../../helpers/urls';
import { LatLng } from '../../types/maps';
import { HereAutocompleteItem } from './autocomplete';
import { fetchWithRetry } from '../../helpers/retrier';
import { getStatesPolygonData } from '../../services/shapes';
import { countries } from './countries';
import { provincesAbbreviationList, statesAbbreviationList } from './states';

const { publicRuntimeConfig } = getConfig();

/* istanbul ignore next */
const basePath = publicRuntimeConfig.basePath
  ? publicRuntimeConfig.basePath
  : '';

export interface ShapeResult {
  [label: string]: LatLng[][];
}

const countryMapping = {};
countries.forEach((country) => {
  countryMapping[country.alpha2] = country.alpha2;
  countryMapping[country.alpha3] = country.alpha2;
});
const usStatesHash = statesAbbreviationList.reduce(
  (hash, stateAbb) => ({ ...hash, [stateAbb]: true }),
  {}
);
const caProvincesHash = provincesAbbreviationList.reduce(
  (hash, stateAbb) => ({ ...hash, [stateAbb]: true }),
  {}
);
const allowedStatesHash = { ...usStatesHash, ...caProvincesHash };

const fetchStateShapes = async (countryCode, stateAbbreviation) => {
  const response = await fetch(
    `${basePath}/assets/static/polygons/${countryCode}/${stateAbbreviation}.json`
  );
  const responseJson = await response.json();
  return responseJson;
};

const fetchStatesShapes = async (states): Promise<ShapeResult> => {
  const polygons = {};
  for (let i = 0; i < states.length; i++) {
    const upperCountryCode = states[i].address?.countryCode?.toUpperCase();
    const upperStateCode = states[i].address?.stateCode?.toUpperCase();
    if (allowedStatesHash[upperStateCode]) {
      // eslint-disable-next-line no-await-in-loop
      const stateShape = await fetchStateShapes(
        upperCountryCode,
        upperStateCode
      );
      polygons[
        `${countryMapping[upperCountryCode]}-${states[i].address?.stateCode}`
      ] = stateShape.shape;
    }
  }
  return polygons;
};

const fetchLocationShapes = async (
  items: HereAutocompleteItem[]
): Promise<ShapeResult> => {
  const url = buildApiUrl('shapes');
  const citiesItems = [];
  const statesItems = [];
  items?.forEach((item) => {
    if (item.address?.countryCode && item.address?.stateCode) {
      if (item.address.city) {
        citiesItems.push(item);
      } else {
        statesItems.push(item);
      }
    }
  });
  const [citiesPolygons, statesPolygons] = await Promise.all([
    citiesItems?.length
      ? fetchWithRetry('fetchLocationShapes', url, {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(citiesItems.map((item) => item.address)),
        }).then((result) => {
          if (result.status !== 200) {
            Bugsnag.notify(new Error('Failed fetching city polygons'));
            return {};
          }
          return result.json();
        })
      : {},
    statesItems?.length
      ? getStatesPolygonData(statesItems.map((item) => item.address)).catch(
          (e) => {
            Bugsnag.notify(`Failed fetching state polygons: ${e}`);
            return {};
          }
        )
      : {},
  ]);

  return { ...citiesPolygons, ...statesPolygons };
};

export { fetchStatesShapes, fetchLocationShapes };
